import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useChatWidget } from '../contexts/ChatWidgetContext';
import styles from './UserProfileDropdown.module.css';

/* Matches Ohana-project-1 menu offset (--gap-icon: 4px). */
const PANEL_GAP_PX = 4;
const VIEWPORT_PAD_PX = 12;

/**
 * Ohana-style user menu: same actions/visibility as legacy Header NavDropdown.
 * Shown from the domestic header.
 */
export default function UserProfileDropdown({
  displayName,
  email,
  currentLanguage,
  onLanguageChange,
  showUserProfile,
  onUserProfile,
  showInformationCenter,
  informationCenterLabel,
  onInformationCenter,
  onChangePassword,
  showAdminUserSearch,
  onAdminUserSearch,
  showAdminCpSearch,
  onAdminCpSearch,
  showEtaRuleMaintenance,
  onEtaRuleMaintenance,
  showEtaExceptionMaintenance,
  onEtaExceptionMaintenance,
  showAssumeUser,
  assumeUserIsActive,
  onAssumeUser,
  onReleaseAssumeUser,
  showUserGuide,
  onUserGuide,
  showChat,
  showContactUs = true,
  onContactUs,
  onLogout,
  version,
  avatarInitial,
  avatarUrl,
}) {
  const [open, setOpen] = useState(false);
  const [panelMaxHeight, setPanelMaxHeight] = useState(undefined);
  const wrapRef = useRef(null);
  const { openPanel } = useChatWidget();

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelMaxHeight(undefined);
      return undefined;
    }

    const updateMaxHeight = () => {
      if (!wrapRef.current) return;
      const bottom = wrapRef.current.getBoundingClientRect().bottom;
      const available =
        window.innerHeight - bottom - PANEL_GAP_PX - VIEWPORT_PAD_PX;
      if (available > 0) {
        setPanelMaxHeight(available);
      }
    };

    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        close();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const langEnActive =
    !currentLanguage ||
    currentLanguage === 'en' ||
    currentLanguage.startsWith('en');
  const langZhActive =
    currentLanguage === 'zh' || currentLanguage.startsWith('zh');

  const run = (fn) => {
    close();
    fn && fn();
  };

  const showAdminBlock =
    showAdminUserSearch ||
    showAdminCpSearch ||
    showEtaRuleMaintenance ||
    showEtaExceptionMaintenance ||
    showAssumeUser;

  const showSupportBlock = showUserGuide || showChat || showContactUs;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="header-nav-dropdown"
        id="header-user-profile-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.avatar} aria-hidden>
          {avatarUrl ? (
            <img className={styles.avatarImg} src={avatarUrl} alt="" />
          ) : (
            avatarInitial || '?'
          )}
        </span>
        <span className={styles.triggerName}>{displayName}</span>
        <span className={styles.chevron} aria-hidden>
          ▼
        </span>
      </button>
      {open && (
        <div
          className={styles.panel}
          role="menu"
          aria-labelledby="header-user-profile-dropdown-trigger"
          style={panelMaxHeight ? { maxHeight: panelMaxHeight } : undefined}
        >
          <div className={styles.panelBody}>
            <div className={styles.panelHeader}>
              <div className={styles.userName}>{displayName}</div>
              {email ? <div className={styles.userEmail}>{email}</div> : null}
            </div>
            <hr className={styles.divider} />

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Language</h3>
              <div className={styles.langRow}>
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.langOption} ${
                    langEnActive ? styles.langOptionActive : ''
                  }`}
                  onClick={() => run(() => onLanguageChange('en'))}
                  data-testid="menu_lang_english"
                >
                  English
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.langOption} ${
                    langZhActive ? styles.langOptionActive : ''
                  }`}
                  onClick={() => run(() => onLanguageChange('zh'))}
                  data-testid="menu_lang_mandarin"
                >
                  Mandarin
                </button>
              </div>
            </div>
            <hr className={styles.divider} />

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>My Account</h3>
              {showUserProfile ? (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.linkBtn}
                  onClick={() => run(onUserProfile)}
                >
                  User Profile
                </button>
              ) : null}
              {showInformationCenter ? (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.linkBtn}
                  onClick={() => run(onInformationCenter)}
                >
                  {informationCenterLabel}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className={styles.linkBtn}
                onClick={() => run(onChangePassword)}
              >
                Change Password
              </button>
            </div>
            {showAdminBlock || showSupportBlock ? (
              <hr className={styles.divider} />
            ) : null}

            {showAdminBlock ? (
              <>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Admin Tools</h3>
                  {showAdminUserSearch ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.linkBtn}
                      onClick={() => run(onAdminUserSearch)}
                    >
                      Admin - User Search
                    </button>
                  ) : null}
                  {showAdminCpSearch ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.linkBtn}
                      onClick={() => run(onAdminCpSearch)}
                    >
                      Admin - CP Search
                    </button>
                  ) : null}
                  {showEtaRuleMaintenance ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.linkBtn}
                      onClick={() => run(onEtaRuleMaintenance)}
                    >
                      ETA Rule Maintenance
                    </button>
                  ) : null}
                  {showEtaExceptionMaintenance ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.linkBtn}
                      onClick={() => run(onEtaExceptionMaintenance)}
                    >
                      ETA Rule Exception Maintenance
                    </button>
                  ) : null}
                  {showAssumeUser ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.linkBtn}
                      onClick={() =>
                        run(
                          assumeUserIsActive
                            ? onReleaseAssumeUser
                            : onAssumeUser
                        )
                      }
                    >
                      {assumeUserIsActive
                        ? 'Release Assumed User'
                        : 'Assume User'}
                    </button>
                  ) : null}
                </div>
                {showSupportBlock ? <hr className={styles.divider} /> : null}
              </>
            ) : null}

            {showSupportBlock ? (
              <div className={`${styles.section} ${styles.supportBlock}`}>
                <h3 className={styles.sectionTitle}>Support</h3>
                {showUserGuide ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.linkBtn}
                    onClick={() => run(onUserGuide)}
                  >
                    User Guide
                  </button>
                ) : null}
                {showChat ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.linkBtn}
                    onClick={() => run(() => openPanel('header'))}
                  >
                    Chat
                  </button>
                ) : null}
                {showContactUs ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.linkBtn}
                    onClick={() => run(onContactUs)}
                  >
                    Contact us
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.logoutBtn}
              onClick={() => run(onLogout)}
            >
              Logout
            </button>
            {version ? <div className={styles.version}>v{version}</div> : null}
          </div>
        </div>
      )}
    </div>
  );
}
